import React, { useEffect, useRef } from 'react'

import csrfHeaders from 'src/utils/csrfHeaders'

import BackLink from '../BackLink'
import DonationExtras from '../DonationExtras'
import PageHeader from '../PageHeader'

// Final payment step for stock donations. The DonateStock EasyButton
// renders inline; DonationExtras (tribute / business_name / comment,
// each a modal trigger) sits above it; the back button sits below.
// No DataSharing — DonateStock's site has its own privacy controls. And
// because there's no DataSharing here, there's deliberately no plugin
// `afterDataSharing` slot on this step (unlike Contact / CryptoForm /
// ExpressCheckout): stock donations collect no contact/data-sharing info on
// our form, so a plugin scoped only to that slot doesn't run for stock. See
// the "Slots aren't universal" note in the snippet editor tips.
//
// onDonationFormOpen fires when the SDK form opens (after the donor
// clicks the SDK's own button). It's not a true pre-open hook — the
// form is already on screen — but the donor still has to enter security
// symbol / shares / broker details inside DonateStock (~5-30s), which
// is plenty of time for our saveDraft (~200ms) to land before the
// donor commits.
//
// SDK init's externalId is the legacy `partner_id_external_id` form
// because saveDraft runs AFTER init, so we don't yet have an orderUuid
// to pass as `oid_{uuid}`. The webhook normally finds the order via
// processing_id (stamped by our post-success POST); externalId is the
// fallback the webhook's recover_orphan_order path uses on
// webhook-beats-POST races. A future enhancement could pre-saveDraft
// on step mount to enable the tight `oid_{uuid}` linkage, at the cost
// of abandoned drafts from donors who reach this step but never click.
const StockPayment = ({
  i18n, record, partner, partnerItem, extras,
  stockProps, finished, setSubmitInFlight, onBack,
  blank_modal_src, emoji_picker_loader_src, widget_instance_token,
}) => {
  const containerRef = useRef()
  const orderTokenRef = useRef(null)
  // SDK init runs once (deps below are id-stable), so the handlers it
  // closes over would otherwise capture the first render's record.
  // Donor edits to tribute (via the modal on this step) commit
  // post-mount and update `record.saveDraft`'s closure to a fresh one
  // each render, but the SDK has already taken our old handler. Route
  // through refs that update every render so the SDK always invokes
  // the latest closure.
  const onFormOpenRef = useRef()
  const onSuccessfulRef = useRef()

  onFormOpenRef.current = async () => {
    const { errors, token } = await record.saveDraft({
      principalOnly: true,
      order: { payment_source: 'stock' },
    })
    if (errors) {
      // Donor's about to engage with DonateStock. Don't block them —
      // the post-success POST will fail (no token), but the
      // DonateStock webhook will orphan-recover the order via
      // partner_id_external_id.
      Honeybadger.notify(new Error('Stock saveDraft failed at onDonationFormOpen'), {
        name: 'StockDraftError',
        context: { errors },
      })
      return
    }
    orderTokenRef.current = token
  }

  onSuccessfulRef.current = async donationUuid => {
    const token = orderTokenRef.current
    setSubmitInFlight(true)
    try {
      if (!token) {
        throw new Error('Stock: no order token at onDonationSuccessful (saveDraft on form-open likely failed)')
      }
      const response = await fetch(stockProps.submitUrl, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({ order_id: token, processing_id: donationUuid }),
      })
      if (!response.ok) {
        throw new Error('Server side validation failed', {
          cause: Error(JSON.stringify(await response.json(), null, 2)),
        })
      }
    } catch (error) {
      // The DonateStock UUID we hand the webhook is enough to settle
      // the order on its side regardless — via processing_id if the
      // POST stamped it, otherwise via the webhook's orphan-recovery
      // path. Worst case is a feed delay. Don't alarm the donor.
      console.error(error)
      Honeybadger.notify(error, {
        name: 'StockPostSuccessError',
        context: { token, donationUuid },
      })
    } finally {
      // partnerItem is enough for the ThankYou panel; amount / email
      // come from DonateStock later and will land on the Order via
      // the webhook. ThankYou's amount-message line is hidden when
      // amount is absent (see ThankYou's pending branch).
      finished({ partnerItem, token, pending: true })
    }
  }

  useEffect(() => {
    if (!containerRef.current || partnerItem.beneficiary.id == '_multiple') return

    while (containerRef.current.firstChild)
      containerRef.current.removeChild(containerRef.current.firstChild)

    const anotherContainer = document.createElement('div')
    containerRef.current.appendChild(anotherContainer)

    window['DonateStock-main'] = {
      q: [['init', {
        element: anotherContainer,
        externalId: `${partner.id}_${partnerItem.external_id}`,
        fboEin: partnerItem.beneficiary.ein.replace(/-/g, ''),
        fboName: partnerItem.beneficiary.name,
        partnerCode: stockProps.partnerCode,
        nativeWindow: true,
        preloadNonprofit: false,
        ein: stockProps.ein,
        debug: true,
        style: { dark: true },
        onDonationFormOpen: (...args) => onFormOpenRef.current?.(...args),
        onDonationSuccessful: (...args) => onSuccessfulRef.current?.(...args),
      }]],
    }

    const script = document.createElement('script')
    script.src = stockProps.scriptUrl
    script.async = true
    script.id = 'DonateStock-main'
    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
      window['loaded-DonateStock-main'] = false
      window['DonateStock-main'] = undefined
    }
  }, [ partnerItem.beneficiary.id ])

  return (
    <div className="space-y-4">
      <PageHeader heading={i18n.t('steps.payment.heading')} />

      <DonationExtras
        extras={extras}
        i18n={i18n}
        blank_modal_src={blank_modal_src}
        emoji_picker_loader_src={emoji_picker_loader_src}
        widget_instance_token={widget_instance_token}
      />

      <div ref={containerRef} className="text-center"></div>

      <div className="text-sm text-center bg-gray-100 p-3 rounded-lg">
        <p className="max-w-72 mx-auto">
          {i18n.t('donate_stock.heading')}<br/>
          <a
            href="https://drive.google.com/file/d/1Dh0tApOQhDlzud2F7H8UZMed9Zhp2GVL/view"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            {i18n.t('donate_stock.learn_more')}
          </a>
        </p>
      </div>

      <BackLink i18n={i18n} onClick={onBack} />
    </div>
  )
}

export default StockPayment
